import React, { useState, useRef } from 'react';
import {
    Box, Grid, Stack, Typography, IconButton, Tooltip,
    Avatar, Divider, Popover, InputBase, alpha, useTheme, Button, Chip,
} from '@mui/material';
import { 
    AddRounded, DeleteRounded, FormatColorFillRounded, ImageRounded,
    ChevronLeftRounded, ChevronRightRounded, PlayArrowRounded, PauseRounded
} from '@mui/icons-material';
import Slider from 'react-slick';
import Iconify from '../../Iconify';

// ── Star rating display (editable when isSiteBuilder) ─────────────────────────

const StarRating = ({ value = 5, color, isSiteBuilder = false, onSave }) => {
    const [hovered, setHovered] = React.useState(null);
    const display = hovered !== null ? hovered : value;
    return (
        <Stack direction="row" spacing={0.25}
            onMouseLeave={() => isSiteBuilder && setHovered(null)}
        >
            {Array.from({ length: 5 }).map((_, i) => (
                <Iconify key={i}
                    icon={i < display ? 'eva:star-fill' : 'eva:star-outline'}
                    onMouseEnter={() => isSiteBuilder && setHovered(i + 1)}
                    onClick={() => { if (isSiteBuilder && onSave) { onSave(i + 1); setHovered(null); } }}
                    sx={{
                        width: 16, height: 16,
                        color: i < display ? color : alpha(color, 0.25),
                        cursor: isSiteBuilder ? 'pointer' : 'default',
                        transition: 'transform 0.1s',
                        transform: isSiteBuilder && hovered === i + 1 ? 'scale(1.3)' : 'scale(1)',
                    }}
                />
            ))}
        </Stack>
    );
};

// ── Editable text helper ───────────────────────────────────────────────────────

const normalizeEditableHtml = (html) => {
    if (!html) return '';
    let res = html.trim();
    if (res === '<br>' || res === '<p><br></p>') return '';
    return res;
};

const E = ({ value, isSiteBuilder, onSave, variant = 'body2', fontWeight, color = 'text.primary', sx = {}, placeholder = '' }) => (
    <Typography
        component="div"
        variant={variant}
        fontWeight={fontWeight}
        contentEditable={isSiteBuilder}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onClick={(e) => isSiteBuilder && e.stopPropagation()}
        onFocus={(e) => isSiteBuilder && e.stopPropagation()}
        onBlur={(e) => isSiteBuilder && onSave(normalizeEditableHtml(e.currentTarget.innerHTML))}
        onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSave(normalizeEditableHtml(e.currentTarget.innerHTML));
                e.currentTarget.blur();
            }
        }}
        sx={{
            outline: 'none',
            cursor: isSiteBuilder ? 'text' : 'default',
            color,
            minHeight: '1em',
            '&:empty:before': isSiteBuilder ? { content: `attr(data-placeholder)`, color: 'text.disabled', fontStyle: 'italic' } : {},
            ...sx
        }}
        dangerouslySetInnerHTML={{ __html: typeof value === 'string' ? value : String(value ?? '') }}
    />
);

// ── Layout variants ────────────────────────────────────────────────────────────

const LAYOUTS = [
    { value: 'card', label: 'Card', icon: 'eva:square-outline' },
    { value: 'carousel', label: 'Carousel Slider', icon: 'eva:move-outline' },
    { value: 'image-top', label: 'Image top', icon: 'eva:person-outline' },
    { value: 'featured', label: 'Featured image', icon: 'eva:image-outline' },
    { value: 'horizontal', label: 'Horizontal', icon: 'eva:menu-outline' },
    { value: 'bubble', label: 'Bubble', icon: 'eva:message-circle-outline' },
    { value: 'minimal', label: 'Minimal', icon: 'eva:minus-outline' },
];

// ── Avatar click helper (safe — guard if no gallery handler) ──────────────────

const ClickableAvatar = ({ item, size, color, isSiteBuilder, onOpenGallery }) => (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
            src={item.image || ''}
            sx={{ width: size, height: size, bgcolor: alpha(color, 0.15), color, fontSize: size * 0.4, cursor: isSiteBuilder ? 'pointer' : 'default', border: `2px solid ${alpha(color, 0.2)}` }}
            onClick={() => isSiteBuilder && typeof onOpenGallery === 'function' && onOpenGallery(item.id)}
        >
            {!item.image && (item.name?.[0]?.toUpperCase() || '?')}
        </Avatar>
        {isSiteBuilder && (
            <Box
                onClick={() => typeof onOpenGallery === 'function' && onOpenGallery(item.id)}
                sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha('#000', 0.4), opacity: 0, transition: '0.2s',
                    cursor: 'pointer', '&:hover': { opacity: 1 }
                }}
            >
                <ImageRounded sx={{ color: '#fff', fontSize: size > 60 ? 22 : 14 }} />
            </Box>
        )}
    </Box>
);

// ── Single testimonial card ────────────────────────────────────────────────────

const TestimonialCard = ({ item, layout: layoutProp, showImage, showRating, accentColor, isSiteBuilder, onUpdate, onOpenGallery, theme }) => {
    const layout = layoutProp === 'carousel' ? 'card' : layoutProp;
    const color = accentColor || theme.palette.primary.main;
    const upd = (field) => (val) => onUpdate(item.id, field, val);

    const rating = showRating ? (
        <StarRating value={item.rating || 5} color={color}
            isSiteBuilder={isSiteBuilder}
            onSave={(v) => onUpdate(item.id, 'rating', v)} />
    ) : null;

    const authorBlock = (
        <Stack spacing={0.1}>
            <E value={item.name} isSiteBuilder={isSiteBuilder} onSave={upd('name')}
                variant="subtitle2" fontWeight={700} placeholder="Author name" />
            <E value={item.role} isSiteBuilder={isSiteBuilder} onSave={upd('role')}
                variant="caption" color="text.secondary" placeholder="Role, Company" />
        </Stack>
    );

    const quoteText = (
        <E value={item.quote} isSiteBuilder={isSiteBuilder} onSave={upd('quote')}
            variant="body2" color="text.secondary" placeholder="Write testimonial here..."
            sx={{ lineHeight: 1.7, fontStyle: 'italic' }} />
    );

    // ── Image top: centered avatar above content ──
    if (layout === 'image-top') {
        return (
            <Stack spacing={2} alignItems="center" textAlign="center">
                {showImage && (
                    <ClickableAvatar item={item} size={72} color={color}
                        isSiteBuilder={isSiteBuilder} onOpenGallery={onOpenGallery} />
                )}
                {rating}
                {quoteText}
                {authorBlock}
            </Stack>
        );
    }

    // ── Featured: large banner image at top ──
    if (layout === 'featured') {
        return (
            <Stack spacing={0}>
                {showImage && (
                    <Box sx={{ position: 'relative', width: '100%', height: 160, bgcolor: alpha(color, 0.08), borderRadius: '8px 8px 0 0', overflow: 'hidden' }}>
                        {item.image
                            ? <Box component="img" src={item.image} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Iconify icon="eva:person-outline" sx={{ width: 48, height: 48, color: alpha(color, 0.35) }} />
                            </Box>
                        }
                        {isSiteBuilder && (
                            <Box
                                onClick={() => typeof onOpenGallery === 'function' && onOpenGallery(item.id)}
                                sx={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: alpha('#000', 0.35), opacity: 0, transition: '0.2s',
                                    cursor: 'pointer', '&:hover': { opacity: 1 }
                                }}
                            >
                                <ImageRounded sx={{ color: '#fff', fontSize: 28 }} />
                            </Box>
                        )}
                    </Box>
                )}
                <Stack spacing={1.5} sx={{ p: 2.5 }}>
                    {rating}
                    {quoteText}
                    {authorBlock}
                </Stack>
            </Stack>
        );
    }

    // ── Card layout ──
    if (layout === 'card') {
        return (
            <Stack spacing={2}>
                {rating}
                {quoteText}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {showImage && <ClickableAvatar item={item} size={40} color={color} isSiteBuilder={isSiteBuilder} onOpenGallery={onOpenGallery} />}
                    {authorBlock}
                </Stack>
            </Stack>
        );
    }

    // ── Horizontal layout ──
    if (layout === 'horizontal') {
        return (
            <Stack direction="row" spacing={2} alignItems="flex-start">
                {showImage && <Box sx={{ pt: 0.5 }}><ClickableAvatar item={item} size={52} color={color} isSiteBuilder={isSiteBuilder} onOpenGallery={onOpenGallery} /></Box>}
                <Stack spacing={1} flex={1}>
                    {rating}
                    {quoteText}
                    {authorBlock}
                </Stack>
            </Stack>
        );
    }

    // ── Bubble layout ──
    if (layout === 'bubble') {
        return (
            <Stack spacing={1.5}>
                <Typography sx={{ fontSize: '3rem', lineHeight: 0.8, color, fontFamily: 'Georgia, serif', userSelect: 'none' }}>&ldquo;</Typography>
                {quoteText}
                {rating}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                    {showImage && <ClickableAvatar item={item} size={36} color={color} isSiteBuilder={isSiteBuilder} onOpenGallery={onOpenGallery} />}
                    {authorBlock}
                </Stack>
            </Stack>
        );
    }

    // ── Minimal layout ──
    return (
        <Stack spacing={1.5}>
            {rating}
            {quoteText}
            <Divider sx={{ borderColor: alpha(color, 0.2) }} />
            <Stack direction="row" spacing={1.5} alignItems="center">
                {showImage && <ClickableAvatar item={item} size={36} color={color} isSiteBuilder={isSiteBuilder} onOpenGallery={onOpenGallery} />}
                {authorBlock}
            </Stack>
        </Stack>
    );
};

// ── Card wrapper styles per layout ────────────────────────────────────────────

const cardBoxSx = (layout, theme, accentColor) => {
    const color = accentColor || theme.palette.primary.main;
    if (layout === 'minimal') return {
        p: 2,
        borderLeft: `3px solid ${color}`,
        pl: 2.5,
    };
    if (layout === 'bubble') return {
        p: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.85),
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        border: '1px solid', borderColor: 'divider',
    };
    if (layout === 'horizontal') return {
        p: 2.5,
        bgcolor: alpha(theme.palette.background.paper, 0.7),
        borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
    };
    if (layout === 'image-top') return {
        p: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid', borderColor: 'divider',
    };
    if (layout === 'featured') return {
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        borderRadius: 2,
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        border: '1px solid', borderColor: 'divider',
        overflow: 'hidden',
        p: 0,
    };
    // card (default)
    return {
        p: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid', borderColor: 'divider',
    };
};

// ── Main component ─────────────────────────────────────────────────────────────

const CommonTestimonialGrid = ({
    items = [],
    layout: layoutProp = 'card',
    cols: colsProp = 2,
    showImage: showImageProp = true,
    showRating: showRatingProp = true,
    accentColor: accentColorProp,
    isScroll: isScrollProp = false,
    isAutoplay: isAutoplayProp = false,
    isSiteBuilder = false,
    isActive = false,
    onUpdateItems,
    onUpdateBlock,
    onOpenGallery,
}) => {
    const theme = useTheme();
    const scrollRef = useRef(null);
    const [layout, setLayout] = useState(layoutProp);
    const [cols, setCols] = useState(colsProp);
    const [showImage, setShowImage] = useState(showImageProp);
    const [showRating, setShowRating] = useState(showRatingProp);
    const [accentColor, setAccentColor] = useState(accentColorProp);
    const [isScroll, setIsScroll] = useState(isScrollProp);
    const [isAutoplay, setIsAutoplay] = useState(isAutoplayProp);
    const [currentIndex, setCurrentIndex] = useState(0);
    const colorBtnRef = useRef(null);
    const [colorAnchor, setColorAnchor] = useState(null);

    React.useEffect(() => { setLayout(layoutProp); }, [layoutProp]);
    React.useEffect(() => { setCols(colsProp); }, [colsProp]);
    React.useEffect(() => { setShowImage(showImageProp); }, [showImageProp]);
    React.useEffect(() => { setShowRating(showRatingProp); }, [showRatingProp]);
    React.useEffect(() => { setAccentColor(accentColorProp); }, [accentColorProp]);
    React.useEffect(() => { setIsScroll(isScrollProp); }, [isScrollProp]);
    React.useEffect(() => { setIsAutoplay(isAutoplayProp); }, [isAutoplayProp]);

    const handleNext = React.useCallback(() => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const itemWidth = container.clientWidth / cols;
            const nextIndex = (currentIndex + 1) % items.length;
            setCurrentIndex(nextIndex);
            container.scrollTo({
                left: itemWidth * nextIndex,
                behavior: 'smooth'
            });
        }
    }, [currentIndex, items.length, cols]);
 
    const handlePrev = React.useCallback(() => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const itemWidth = container.clientWidth / cols;
            const prevIndex = (currentIndex - 1 + items.length) % items.length;
            setCurrentIndex(prevIndex);
            container.scrollTo({
                left: itemWidth * prevIndex,
                behavior: 'smooth'
            });
        }
    }, [currentIndex, items.length, cols]);
 
    // Autoplay logic
    React.useEffect(() => {
        const isSliderMode = isScroll || layout === 'carousel';
        if (!isSliderMode || !isAutoplay || isSiteBuilder) return;
        const interval = setInterval(() => {
            handleNext();
        }, 5000);
        return () => clearInterval(interval);
    }, [isScroll, layout, isAutoplay, isSiteBuilder, items.length, handleNext]);


    const color = accentColor || theme.palette.primary.main;

    const handleUpdate = (id, field, val) =>
        onUpdateItems(items.map(i => String(i.id) === String(id) ? { ...i, [field]: val } : i));

    const handleAdd = () =>
        onUpdateItems([...items, { id: Date.now(), quote: 'This product changed everything for us. Highly recommend!', name: 'Jane Smith', role: 'CEO, Acme Inc.', image: '', rating: 5 }]);

    const handleDelete = (id) =>
        onUpdateItems(items.filter(i => String(i.id) !== String(id)));

    const applyUpdate = (updates) => {
        if (updates.layout !== undefined) setLayout(updates.layout);
        if (updates.cols !== undefined) setCols(updates.cols);
        if (updates.showImage !== undefined) setShowImage(updates.showImage);
        if (updates.showRating !== undefined) setShowRating(updates.showRating);
        if (updates.accentColor !== undefined) setAccentColor(updates.accentColor);
        if (updates.isScroll !== undefined) setIsScroll(updates.isScroll);
        if (updates.isAutoplay !== undefined) setIsAutoplay(updates.isAutoplay);
        onUpdateBlock(updates);
    };

    const gridMd = Math.floor(12 / cols);
    const isSliderMode = isScroll || layout === 'carousel';

    return (
        <Box sx={{ width: '100%', my: 2, position: 'relative' }}>

            {/* ── Toolbar ───────────────────────────────────────────────────── */}
            {isSiteBuilder && isActive && (
                <Stack direction="row" spacing={0.5} alignItems="center"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute', top: -48, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 1000, p: 0.5, bgcolor: 'background.paper',
                        boxShadow: 20, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {isSliderMode && (
                        <Box sx={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)' }}>
                            <Chip label="Slider Mode Active" size="small" color="primary" sx={{ height: 16, fontSize: 9 }} />
                        </Box>
                    )}
 
                    {/* Layout */}
                    {LAYOUTS.map(l => (
                        <Tooltip key={l.value} title={l.label}>
                            <IconButton size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.stopPropagation(); applyUpdate({ layout: l.value }); }}
                                sx={{ borderRadius: 0.5, bgcolor: layout === l.value ? alpha(color, 0.12) : 'transparent', color: layout === l.value ? 'primary.main' : 'inherit' }}>
                                <Iconify icon={l.icon} sx={{ width: 15, height: 15 }} />
                            </IconButton>
                        </Tooltip>
                    ))}

                    <Divider orientation="vertical" flexItem />

                    {/* Columns */}
                    {['1', '2', '3'].map(c => (
                        <Tooltip key={c} title={`${c} column${Number(c) > 1 ? 's' : ''}`}>
                            <IconButton size="small"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.stopPropagation(); applyUpdate({ cols: Number(c) }); }}
                                sx={{ borderRadius: 0.5, minWidth: 28, bgcolor: cols === Number(c) ? alpha(color, 0.12) : 'transparent', color: cols === Number(c) ? 'primary.main' : 'inherit' }}>
                                <Typography variant="caption" fontWeight={700}>{c}</Typography>
                            </IconButton>
                        </Tooltip>
                    ))}

                    <Divider orientation="vertical" flexItem />

                    {/* Show image toggle */}
                    <Tooltip title={showImage ? 'Hide images' : 'Show images'}>
                        <IconButton size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => { e.stopPropagation(); applyUpdate({ showImage: !showImage }); }}
                            sx={{ borderRadius: 0.5, bgcolor: showImage ? alpha(color, 0.12) : 'transparent', color: showImage ? 'primary.main' : 'text.disabled' }}>
                            <Iconify icon="eva:person-outline" sx={{ width: 15, height: 15 }} />
                        </IconButton>
                    </Tooltip>

                    {/* Show rating toggle */}
                    <Tooltip title={showRating ? 'Hide stars' : 'Show stars'}>
                        <IconButton size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => { e.stopPropagation(); applyUpdate({ showRating: !showRating }); }}
                            sx={{ borderRadius: 0.5, bgcolor: showRating ? alpha(color, 0.12) : 'transparent', color: showRating ? 'primary.main' : 'text.disabled' }}>
                            <Iconify icon="eva:star-outline" sx={{ width: 15, height: 15 }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={isScroll ? 'Grid View' : 'Scroll View'}>
                        <IconButton size="small"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => { e.stopPropagation(); applyUpdate({ isScroll: !isScroll }); }}
                            sx={{ borderRadius: 0.5, bgcolor: isScroll ? alpha(color, 0.12) : 'transparent', color: isScroll ? 'primary.main' : 'text.disabled' }}>
                            <Iconify icon={isScroll ? 'eva:grid-outline' : 'eva:move-outline'} sx={{ width: 15, height: 15 }} />
                        </IconButton>
                    </Tooltip>

                    {isSliderMode && (
                        <Tooltip title={isAutoplay ? 'Disable Auto-scroll' : 'Enable Auto-scroll'}>
                            <IconButton size="small"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); applyUpdate({ isAutoplay: !isAutoplay }); }}
                                sx={{ borderRadius: 0.5, bgcolor: isAutoplay ? alpha(color, 0.12) : 'transparent', color: isAutoplay ? 'primary.main' : 'text.disabled' }}>
                                {isAutoplay ? <PauseRounded sx={{ fontSize: 16 }} /> : <PlayArrowRounded sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    )}

                    <Divider orientation="vertical" flexItem />

                    {/* Accent color */}
                    <Tooltip title="Accent Color">
                        <IconButton ref={colorBtnRef} size="small"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setColorAnchor(colorBtnRef.current); }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <FormatColorFillRounded fontSize="small" />
                                <Box sx={{ width: 14, height: 3, borderRadius: '2px', bgcolor: color }} />
                            </Box>
                        </IconButton>
                    </Tooltip>

                    <Popover open={Boolean(colorAnchor)} anchorEl={colorAnchor}
                        onClose={() => setColorAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        onClick={(e) => e.stopPropagation()} sx={{ zIndex: 9999 }}>
                        <Stack spacing={1} sx={{ p: 1.5, minWidth: 160 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Accent Color</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <input type="color" value={color}
                                    onChange={(e) => applyUpdate({ accentColor: e.target.value })}
                                    style={{ width: 36, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
                                <InputBase value={color}
                                    onChange={(e) => applyUpdate({ accentColor: e.target.value })}
                                    inputProps={{ maxLength: 7, style: { fontFamily: 'monospace', fontSize: 13 } }}
                                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, flex: 1, height: 36 }} />
                            </Stack>
                        </Stack>
                    </Popover>
                </Stack>
            )}

            {/* ── Testimonial grid ──────────────────────────────────────────── */}
            {isSliderMode ? (
                <Box sx={{ width: '100%', position: 'relative' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', px: { xs: 0, md: 2 } }}>
                        {/* Navigation Arrow Left */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <IconButton
                                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                                sx={{
                                    bgcolor: 'background.paper', boxShadow: 2,
                                    border: '1px solid', borderColor: 'divider',
                                    '&:hover': { bgcolor: 'background.paper', color: 'primary.main', transform: 'scale(1.1)' }
                                }}
                            >
                                <ChevronLeftRounded />
                            </IconButton>
                        </Box>
 
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Box
                                ref={scrollRef}
                                sx={{
                                    display: 'flex',
                                    overflowX: 'hidden',
                                    scrollSnapType: 'x mandatory',
                                    scrollBehavior: 'smooth',
                                    WebkitOverflowScrolling: 'touch',
                                    '& > *': { flex: `0 0 ${100 / cols}%`, scrollSnapAlign: 'start' }
                                }}
                            >
                                {items.map((item) => (
                                    <Box key={item.id} sx={{ px: { xs: 0.5, md: 1.5 }, py: 1 }}>
                                        <Box
                                            className="testimonial-card"
                                            sx={{
                                                position: 'relative',
                                                ...cardBoxSx(layout, theme, accentColor),
                                                mx: 'auto',
                                                maxWidth: 800,
                                                height: '100%',
                                                '&:hover .t-del': { opacity: 1 },
                                            }}
                                        >
                                            {isSiteBuilder && (
                                                <IconButton className="t-del" size="small" color="error"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: layout === 'featured' ? 8 : 6,
                                                        right: layout === 'featured' ? 8 : 6,
                                                        opacity: 0, transition: '0.15s',
                                                        bgcolor: alpha(theme.palette.background.paper, 0.9), zIndex: 2
                                                    }}>
                                                    <DeleteRounded sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            )}
                                            <TestimonialCard
                                                item={item}
                                                layout={layout}
                                                showImage={showImage}
                                                showRating={showRating}
                                                accentColor={accentColor}
                                                isSiteBuilder={isSiteBuilder}
                                                onUpdate={handleUpdate}
                                                onOpenGallery={onOpenGallery}
                                                theme={theme}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
 
                            {/* Pagination Dots */}
                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                {items.map((_, idx) => (
                                    <Box
                                        key={idx}
                                        onClick={() => {
                                            setCurrentIndex(idx);
                                            const itemWidth = scrollRef.current?.clientWidth / cols;
                                            scrollRef.current?.scrollTo({ left: itemWidth * idx, behavior: 'smooth' });
                                        }}
                                        sx={{
                                            width: 8, height: 8, borderRadius: '50%', cursor: 'pointer',
                                            bgcolor: currentIndex === idx ? color : alpha(color, 0.3),
                                            transform: currentIndex === idx ? 'scale(1.2)' : 'scale(1)',
                                            transition: '0.2s'
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
 
                        {/* Navigation Arrow Right */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <IconButton
                                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                                sx={{
                                    bgcolor: 'background.paper', boxShadow: 2,
                                    border: '1px solid', borderColor: 'divider',
                                    '&:hover': { bgcolor: 'background.paper', color: 'primary.main', transform: 'scale(1.1)' }
                                }}
                            >
                                <ChevronRightRounded />
                            </IconButton>
                        </Box>
                    </Stack>
 
                    {isSiteBuilder && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button size="small" startIcon={<AddRounded />} onClick={handleAdd} sx={{ color: 'text.secondary' }}>
                                Add Testimonial
                            </Button>
                        </Box>
                    )}
                </Box>
            ) : (
                <Grid container spacing={layout === 'minimal' ? 2 : 3}>
                    {items.map((item) => (
                        <Grid item xs={12} sm={cols === 1 ? 12 : 6} md={gridMd} key={item.id}>
                            <Box
                                className="testimonial-card"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    position: 'relative',
                                    height: '100%',
                                    transition: 'all 0.2s',
                                    '&:hover .t-del': { opacity: 1 },
                                    ...cardBoxSx(layout, theme, accentColor),
                                }}
                            >
                                {isSiteBuilder && (
                                    <IconButton className="t-del" size="small" color="error"
                                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                        sx={{
                                            position: 'absolute',
                                            top: layout === 'featured' ? 8 : 6,
                                            right: layout === 'featured' ? 8 : 6,
                                            opacity: 0, transition: '0.15s',
                                            bgcolor: alpha(theme.palette.background.paper, 0.9), zIndex: 2
                                        }}>
                                        <DeleteRounded sx={{ fontSize: 14 }} />
                                    </IconButton>
                                )}

                                <TestimonialCard
                                    item={item}
                                    layout={layout}
                                    showImage={showImage}
                                    showRating={showRating}
                                    accentColor={accentColor}
                                    isSiteBuilder={isSiteBuilder}
                                    onUpdate={handleUpdate}
                                    onDelete={handleDelete}
                                    onOpenGallery={onOpenGallery}
                                    theme={theme}
                                />
                            </Box>
                        </Grid>
                    ))}

                    {/* Add card */}
                    {isSiteBuilder && (
                        <Grid item xs={12} sm={cols === 1 ? 12 : 6} md={gridMd}>
                            <Box onClick={(e) => { e.stopPropagation(); handleAdd(); }}
                                sx={{
                                    height: '100%', minHeight: 120,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                                    cursor: 'pointer', color: 'text.secondary', transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(0,0,0,0.02)' }
                                }}>
                                <AddRounded />
                                <Typography variant="caption" sx={{ mt: 0.5 }}>Add Testimonial</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default CommonTestimonialGrid;
